'use client';

import type { BlogPost, BlogContentBlock } from '@/content/blog/types';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import BlogRating from './BlogRating';

interface BlogArticleProps {
  post: BlogPost;
  currentLanguage: 'en' | 'de';
  prevPost: BlogPost | null;
  nextPost: BlogPost | null;
}

export default function BlogArticle({
  post,
  currentLanguage,
  prevPost,
  nextPost,
}: BlogArticleProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return currentLanguage === 'en' ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const translations = {
    en: {
      by: 'by',
      sources: 'Sources & References',
      backToBlog: '← Back to Blog',
      previous: '← Previous',
      next: 'Next →',
      newer: 'Newer',
      older: 'Older',
    },
    de: {
      by: 'von',
      sources: 'Quellen & Referenzen',
      backToBlog: '← Zurück zum Blog',
      previous: '← Vorheriger',
      next: 'Nächster →',
      newer: 'Neuer',
      older: 'Älter',
    },
  };

  const renderContentBlock = (block: BlogContentBlock, index: number) => {
    switch (block.type) {
      case 'p':
        return (
          <p key={index} className="text-foreground-light text-lg leading-relaxed mb-6">
            {block.text}
          </p>
        );
      case 'p-rich':
        return (
          <p key={index} className="text-foreground-light text-lg leading-relaxed mb-6">
            {block.segments.map((segment, segIndex) => {
              if (segment.type === 'link') {
                return (
                  <Link
                    key={segIndex}
                    href={segment.href}
                    className="text-accent hover:text-accent-light underline transition-colors duration-200"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {segment.content}
                  </Link>
                );
              }
              return <span key={segIndex}>{segment.content}</span>;
            })}
          </p>
        );
      case 'h2':
        return (
          <h2
            key={index}
            className="font-heading font-bold text-3xl text-foreground-light mt-12 mb-6"
          >
            {block.text}
          </h2>
        );
      case 'h3':
        return (
          <h3
            key={index}
            className="font-heading font-semibold text-2xl text-foreground-light mt-8 mb-4"
          >
            {block.text}
          </h3>
        );
      case 'ul':
        return (
          <ul key={index} className="list-disc list-inside space-y-3 mb-6 text-foreground-light">
            {block.items.map((item, i) => (
              <li key={i} className="text-lg leading-relaxed ml-4">
                {item}
              </li>
            ))}
          </ul>
        );
      case 'ol':
        return (
          <ol key={index} className="list-decimal list-inside space-y-3 mb-6 text-foreground-light">
            {block.items.map((item, i) => (
              <li key={i} className="text-lg leading-relaxed ml-4">
                {item}
              </li>
            ))}
          </ol>
        );
      case 'quote':
        return (
          <blockquote
            key={index}
            className="border-l-4 border-accent pl-6 py-4 my-8 bg-secondary/30 rounded-r-lg"
          >
            <p className="text-foreground-light text-xl italic leading-relaxed mb-2">
              "{block.text}"
            </p>
            {block.by && (
              <cite className="text-foreground-secondary text-sm not-italic">— {block.by}</cite>
            )}
          </blockquote>
        );
      case 'image':
        return (
          <figure key={index} className="my-10">
            <div className="relative w-full h-96 rounded-lg overflow-hidden">
              <AppImage
                src={block.src}
                alt={block.alt}
                fill
                sizes="(max-width: 768px) 100vw, 896px"
                loading="lazy"
                className="object-cover"
              />
            </div>
            {block.caption && (
              <figcaption className="text-foreground-muted text-sm text-center mt-3 italic">
                {block.caption}
              </figcaption>
            )}
          </figure>
        );
      default:
        return null;
    }
  };

  return (
    <article className="relative z-10 min-h-screen px-4 py-20">
      <div className="container mx-auto max-w-5xl">
        {/* Article Container with translucent panel */}
        <div className="bg-secondary/80 backdrop-blur-md rounded-2xl shadow-cosmic p-8 md:p-16">
          {/* Header */}
          <header className="mb-10">
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {(currentLanguage === 'de' && post.tagsDe ? post.tagsDe : post.tags).map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-accent/20 text-accent rounded-full text-xs font-semibold uppercase tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-foreground-light mb-6 leading-tight">
              {currentLanguage === 'de' && post.titleDe ? post.titleDe : post.title}
            </h1>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-foreground-secondary text-sm">
              <span>
                {translations[currentLanguage].by} <strong>{post.author}</strong>
              </span>
              <span>•</span>
              <time dateTime={post.date}>{formatDate(post.date)}</time>
              {post.readingTime && (
                <>
                  <span>•</span>
                  <span>{currentLanguage === 'de' && post.readingTimeDe ? post.readingTimeDe : post.readingTime}</span>
                </>
              )}
            </div>
          </header>

          {/* Hero Image */}
          <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
            <AppImage
              src={post.coverImage}
              alt={currentLanguage === 'de' && post.titleDe ? post.titleDe : post.title}
              fill
              sizes="(max-width: 768px) 100vw, 1152px"
              priority
              className="object-cover"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {(currentLanguage === 'de' && post.contentDe ? post.contentDe : post.content).map((block, index) => renderContentBlock(block, index))}
          </div>

          {/* Sources Section */}
          {post.sources && post.sources.length > 0 && (
            <section className="mt-16 pt-8 border-t border-border">
              <h2 className="font-heading font-bold text-2xl text-foreground-light mb-6">
                {translations[currentLanguage].sources}
              </h2>
              <ul className="space-y-3">
                {post.sources.map((source, index) => (
                  <li key={index}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-accent hover:text-highlight transition-colors duration-fast group"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Rating Section */}
          <BlogRating blogSlug={post.slug} currentLanguage={currentLanguage} />
        </div>

        {/* Prev/Next Navigation */}
        {(prevPost || nextPost) && (
          <nav className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Previous (Newer) Post */}
            {prevPost ? (
              <Link
                href={`/blog/${prevPost.slug}`}
                className="group bg-secondary/60 backdrop-blur-sm rounded-xl p-6 hover:bg-secondary/80 transition-all duration-fast hover:shadow-glow-cyan border border-border hover:border-accent"
              >
                <div className="text-accent text-sm font-semibold mb-2 flex items-center">
                  <svg
                    className="w-4 h-4 mr-2 transition-transform duration-fast group-hover:-translate-x-1"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M15 19l-7-7 7-7" />
                  </svg>
                  {translations[currentLanguage].previous} ({translations[currentLanguage].newer})
                </div>
                <h3 className="text-foreground-light font-heading font-semibold text-lg line-clamp-2 group-hover:text-accent transition-colors duration-fast">
                  {currentLanguage === 'de' && prevPost.titleDe ? prevPost.titleDe : prevPost.title}
                </h3>
              </Link>
            ) : (
              <div /> // Empty div for grid alignment
            )}

            {/* Next (Older) Post */}
            {nextPost && (
              <Link
                href={`/blog/${nextPost.slug}`}
                className="group bg-secondary/60 backdrop-blur-sm rounded-xl p-6 hover:bg-secondary/80 transition-all duration-fast hover:shadow-glow-cyan border border-border hover:border-accent text-right"
              >
                <div className="text-accent text-sm font-semibold mb-2 flex items-center justify-end">
                  {translations[currentLanguage].next} ({translations[currentLanguage].older})
                  <svg
                    className="w-4 h-4 ml-2 transition-transform duration-fast group-hover:translate-x-1"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <h3 className="text-foreground-light font-heading font-semibold text-lg line-clamp-2 group-hover:text-accent transition-colors duration-fast">
                  {currentLanguage === 'de' && nextPost.titleDe ? nextPost.titleDe : nextPost.title}
                </h3>
              </Link>
            )}
          </nav>
        )}
      </div>
    </article>
  );
}
